import { Patient } from '@aldebaran/prisma';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useQuestionnaireNavigation } from '@/context/NavigationProvider';
import { useUpdatePatient } from '@/hooks/patient.hooks';
import { useStatistics } from '@/hooks/useStatistics';
import { updatePatient as updatePatientInStore } from '@/reducers/graph';
import { RootState } from '@/types/graph';
import { getRejectedFields, SAVE_ERROR_MESSAGE } from '@/util/apiErrors';

type SaveOptions = {
  onRejectedFields?: (fields: string[]) => boolean;
  onSaved?: (patient: Patient) => void;
};

export const useSavePatientStep = (section: string) => {
  const patient = useSelector((state: RootState) => state.consultation.patient);
  const { navigateNext } = useQuestionnaireNavigation();
  const dispatch = useDispatch();
  const recordStatistics = useStatistics();
  const { mutate: updatePatient, isLoading } = useUpdatePatient();
  const [submitError, setSubmitError] = useState<string>();

  const save = (
    payload: Partial<Patient>,
    { onRejectedFields, onSaved }: SaveOptions = {},
  ) => {
    setSubmitError(undefined);
    // RequirePatient keeps these pages behind a selected patient.
    if (!patient?.id) return;

    updatePatient(
      { id: patient.id, ...payload },
      {
        onSuccess: (savedPatient) => {
          // Keep the store in sync so the field is still there when the patient navigates back to this page.
          dispatch(updatePatientInStore(savedPatient));
          onSaved?.(savedPatient);
          recordStatistics({ section });
          navigateNext();
        },
        onError: (error) => {
          const handled = onRejectedFields?.(getRejectedFields(error)) ?? false;
          if (!handled) setSubmitError(SAVE_ERROR_MESSAGE);
        },
      },
    );
  };

  return {
    patient,
    save,
    isSaving: isLoading,
    submitError,
    clearSubmitError: () => setSubmitError(undefined),
  };
};
